import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Warning, CheckCircle, Bell, Car, CurrencyJpy, CalendarX, MapPin, ShieldCheck } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import StaffDashboard from "./StaffDashboard";
import TripMap from "@/components/TripMap";
import { fmtYmd } from "@/lib/dateFormat";

function Kpi({ label, value, sub, accent, testid }) {
    return (
        <div className="prc-card p-5 relative" data-testid={testid}>
            <div className="prc-corner-tag" />
            <p className="prc-label-overline">{label}</p>
            <p className="prc-metric mt-3" style={accent ? { color: accent } : undefined}>{value}</p>
            {sub && <p className="mt-2 text-xs text-[var(--prc-muted)]">{sub}</p>}
        </div>
    );
}

export default function Dashboard() {
    const { can, user } = useAuth();
    // Staff role gets a role-tailored "cockpit" view instead of the
    // admin-oriented company KPI dashboard.
    if (user?.role === "staff") return <StaffDashboard />;

    return <AdminDashboard can={can} />;
}

function AdminDashboard({ can }) {
    const [kpis, setKpis] = useState(null);
    const [liveTrips, setLiveTrips] = useState([]);
    const [mapTripId, setMapTripId] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [k, lv] = await Promise.all([
                    api.get("/dashboard/kpis"),
                    api.get("/operations/live").catch(() => ({ data: [] })),
                ]);
                setKpis(k.data);
                setLiveTrips(lv.data || []);
            } finally {
                setLoading(false);
            }
        })();
        // Re-poll live trips every 30s while the dashboard is mounted
        const t = setInterval(async () => {
            try {
                const { data } = await api.get("/operations/live");
                setLiveTrips(data || []);
            } catch (e) { /* ignore */ }
        }, 30_000);
        return () => clearInterval(t);
    }, []);

    if (loading) return <div className="prc-label-overline" data-testid="dashboard-loading">読込中...</div>;
    if (!kpis) return <div data-testid="dashboard-error">データ取得エラー</div>;

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline">ダッシュボード</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight">運用サマリ</h1>
                </div>
                <div className="flex gap-2">
                    <Link to="/vehicles" className="prc-btn-ghost text-sm" data-testid="dashboard-vehicles-cta">車両台帳へ</Link>
                    <Link to="/inspect" className="prc-btn-primary text-sm" data-testid="dashboard-inspect-cta">使用前点検を開始</Link>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi label="登録車両台数" value={kpis.total_vehicles} sub={`稼働中 ${kpis.active_vehicles} / 仮登録 ${kpis.draft_vehicles}`} testid="kpi-vehicles" />
                <Kpi label="週次 点検実施率" value={`${kpis.inspection_rate_7d}%`} sub={`直近7日: ${kpis.completed_7d} 件完了 / ${kpis.inspections_7d} 件開始`} testid="kpi-rate" />
                <Kpi label="週次 NG検出件数" value={kpis.ng_7d} sub="7日以内に要対応" accent={kpis.ng_7d > 0 ? "var(--prc-red)" : undefined} testid="kpi-ng" />
                <Kpi label="整備費（30日）" value={`¥${kpis.maint_cost_30d.toLocaleString()}`} sub="直近30日合計" testid="kpi-cost" />
            </div>

            {/* Live operations feed — only renders when there's at least one
                in-progress / paused trip across the company. Auto-refreshes
                every 30s so dispatchers can monitor the fleet in real-time. */}
            {liveTrips.length > 0 && (
                <div className="prc-card p-5" data-testid="live-trips">
                    <div className="flex items-center justify-between mb-3">
                        <p className="prc-label-overline mb-0 !text-[var(--prc-green)]">// ライブ運行 ({liveTrips.length}件)</p>
                        <Link to="/operations" className="text-xs font-semibold hover:underline">運行記録を開く →</Link>
                    </div>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {liveTrips.map((t) => (
                            <li key={t.id} className={`prc-card p-3 border-l-4 ${t.status === "paused" ? "border-l-amber-400" : "border-l-[var(--prc-green)]"}`}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-semibold truncate">{t.driver_name || "-"}</span>
                                    <div className="flex items-center gap-1">
                                        {t._last_gps && (
                                            <button
                                                type="button"
                                                onClick={() => setMapTripId(t.id)}
                                                className="prc-btn-ghost !px-2 !py-0.5 text-[10px] inline-flex items-center gap-1"
                                                data-testid={`live-map-btn-${t.id}`}
                                                title="地図で見る"
                                            >
                                                <MapPin size={16} />地図
                                            </button>
                                        )}
                                        <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm ${t.status === "paused" ? "bg-amber-400/20 text-amber-200" : "bg-[var(--prc-green)]/20 text-[var(--prc-green)]"}`}>
                                            {t.status === "paused" ? "PAUSE" : "LIVE"}
                                        </span>
                                    </div>
                                </div>
                                <p className="text-xs text-[var(--prc-muted)] truncate font-mono">{t.vehicle_plate} · {t.start_time}〜</p>
                                <p className="text-xs truncate"><span className="text-[var(--prc-muted)]">出発:</span> {t.origin || "—"}</p>
                                {t._timing && <p className="text-[10px] font-mono text-[var(--prc-muted)] mt-1">// 経過 {t._timing.elapsed_min}分 (稼働 {t._timing.active_min}分)</p>}
                                {t._last_gps && (
                                    <p className="text-[10px] font-mono text-[var(--prc-green)] mt-0.5 truncate">// GPS {t._last_gps.lat.toFixed(4)}, {t._last_gps.lng.toFixed(4)}</p>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {mapTripId && <TripMap tripId={mapTripId} onClose={() => setMapTripId(null)} />}

            <div className="grid lg:grid-cols-3 gap-4">
                <div className="prc-card p-5 lg:col-span-2">
                    <p className="prc-label-overline mb-3">整備費 推移（月次 / 6ヶ月）</p>
                    <div className="h-60 min-h-[240px]" data-testid="cost-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={kpis.cost_series} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 4" />
                                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={{ stroke: "#d1d5db" }} />
                                <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} axisLine={{ stroke: "#d1d5db" }} />
                                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 2 }} formatter={(v) => `¥${v.toLocaleString()}`} />
                                <Line type="monotone" dataKey="cost" stroke="#2563EB" strokeWidth={2} dot={{ r: 3, fill: "#2563EB" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="prc-card p-5">
                    <p className="prc-label-overline mb-3">クイック アクション</p>
                    <div className="space-y-2">
                        {can("vehicles.create") && (
                            <Link to="/vehicles" className="prc-btn-ghost w-full text-left flex items-center justify-between" data-testid="quick-new-vehicle">
                                <span className="flex items-center gap-2"><Car size={16} />車両を新規登録</span><span>→</span>
                            </Link>
                        )}
                        <Link to="/inspect" className="prc-btn-ghost w-full text-left flex items-center justify-between" data-testid="quick-inspect">
                            <span className="flex items-center gap-2"><ShieldCheck size={16} />使用前点検を実施</span><span>→</span>
                        </Link>
                        {can("reports.generate") && (
                            <Link to="/reports" className="prc-btn-ghost w-full text-left flex items-center justify-between" data-testid="quick-report">
                                <span className="flex items-center gap-2"><CurrencyJpy size={16} />月次PDFレポート出力</span><span>→</span>
                            </Link>
                        )}
                        <Link to="/notifications" className="prc-btn-ghost w-full text-left flex items-center justify-between" data-testid="quick-notifications">
                            <span className="flex items-center gap-2"><Bell size={16} />通知を確認 ({kpis.unread_notifications})</span><span>→</span>
                        </Link>
                    </div>
                </div>
            </div>

            <div className="prc-card p-5">
                <div className="flex justify-between items-center mb-3">
                    <p className="prc-label-overline">車検期限アラート（90日以内）</p>
                    {can("vehicles.view") && (
                        <Link to="/vehicles" className="text-xs font-semibold hover:underline">すべて表示</Link>
                    )}
                </div>
                {kpis.expiring_inspections.length === 0 ? (
                    <p className="text-sm text-[var(--prc-muted)] flex items-center gap-2" data-testid="expiring-empty">
                        <CheckCircle size={16} className="text-[var(--prc-green)]" /> 90日以内に車検満了の車両はありません。
                    </p>
                ) : (
                    <table className="prc-table" data-testid="expiring-table">
                        <thead>
                            <tr><th>登録番号</th><th>メーカー</th><th>車種</th><th>車検満了日</th></tr>
                        </thead>
                        <tbody>
                            {kpis.expiring_inspections.map((v) => (
                                <tr key={v.id} data-testid={`expiring-row-${v.id}`}>
                                    <td className="font-mono font-semibold">{v.plate_number}</td>
                                    <td>{v.make || "-"}</td>
                                    <td>{v.model || "-"}</td>
                                    <td className="num">
                                        <span className="prc-badge warn flex items-center gap-1 inline-flex"><CalendarX size={16} />{fmtYmd(v.inspection_expiry, v.inspection_expiry)}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
